import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SliderGame } from './game/SliderGame';
import { gameConfig } from './gameConfig';
import { AutoSolver } from './algorithm/AutoSolver';
import { 
  CharmArrowUp, 
  CharmArrowDown, 
  CharmArrowLeft, 
  CharmArrowRight,
  CharmArrowUpLeft,
  CharmArrowUpRight,
  CharmArrowDownLeft,
  CharmArrowDownRight
} from './icons';
import './SliderGameApp.css';

const SliderGameApp = () => {
  const [game, setGame] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [m, setM] = useState(6);
  const [n, setN] = useState(6);
  const [k, setK] = useState(8);
  const [tempM, setTempM] = useState(6);
  const [tempN, setTempN] = useState(6);
  const [tempK, setTempK] = useState(8);
  const [animatingSliders, setAnimatingSliders] = useState(new Set());
  const [cellSize, setCellSize] = useState(50);
  const [autoSolver, setAutoSolver] = useState(null);
  const [isSolving, setIsSolving] = useState(false);
  const [instructionType, setInstructionType] = useState(null);
  const [initialSliders, setInitialSliders] = useState([]);
  const [moveHistory, setMoveHistory] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(-1);
  const boardContainerRef = useRef(null);
  const inputMRef = useRef(null);
  const inputNRef = useRef(null);
  const inputKRef = useRef(null);

  // 处理行数输入 - 只允许数字和空值
  const handleMChange = (e) => {
    const value = e.target.value;
    // 只允许数字输入
    if (value === '' || /^\d+$/.test(value)) {
      setTempM(value === '' ? '' : parseInt(value));
    }
  };

  // 处理列数输入 - 只允许数字和空值
  const handleNChange = (e) => {
    const value = e.target.value;
    // 只允许数字输入
    if (value === '' || /^\d+$/.test(value)) {
      setTempN(value === '' ? '' : parseInt(value));
    }
  };

  // 处理滑块数输入 - 只允许数字和空值
  const handleKChange = (e) => {
    const value = e.target.value;
    // 只允许数字输入
    if (value === '' || /^\d+$/.test(value)) {
      setTempK(value === '' ? '' : parseInt(value));
    }
  };

  // 行数输入失去焦点时检查合法性
  const handleMBlur = (e) => {
    let value = e.target.value === '' ? '' : parseInt(e.target.value);
    if (value === '' || isNaN(value)) {
      setTempM(gameConfig.minRows);
      return;
    }
    const clamped = Math.max(gameConfig.minRows, Math.min(gameConfig.maxRows, value));
    if (clamped !== value) {
      setTempM(clamped);
      if (inputMRef.current) {
        inputMRef.current.select();
      }
    }
  };

  // 列数输入失去焦点时检查合法性
  const handleNBlur = (e) => {
    let value = e.target.value === '' ? '' : parseInt(e.target.value);
    if (value === '' || isNaN(value)) {
      setTempN(gameConfig.minCols);
      return;
    }
    const clamped = Math.max(gameConfig.minCols, Math.min(gameConfig.maxCols, value));
    if (clamped !== value) {
      setTempN(clamped);
      if (inputNRef.current) {
        inputNRef.current.select();
      }
    }
  };

  // 滑块数输入失去焦点时检查合法性
  const handleKBlur = (e) => {
    let value = e.target.value === '' ? '' : parseInt(e.target.value);
    if (value === '' || isNaN(value)) {
      // 计算最大滑块数
      const currentM = isNaN(tempM) ? gameConfig.minRows : tempM;
      const currentN = isNaN(tempN) ? gameConfig.minCols : tempN;
      const maxK = Math.floor(currentM * currentN / gameConfig.sliderLength * 0.8);
      setTempK(Math.max(1, maxK));
      return;
    }
    // 计算最大滑块数
    const currentM = isNaN(tempM) ? gameConfig.minRows : tempM;
    const currentN = isNaN(tempN) ? gameConfig.minCols : tempN;
    const maxK = Math.floor(currentM * currentN / gameConfig.sliderLength * 0.8);
    const clamped = Math.max(1, Math.min(maxK, value));
    if (clamped !== value) {
      setTempK(clamped);
      if (inputKRef.current) {
        inputKRef.current.select();
      }
    }
  };

  // 计算响应式格子大小
  const calculateCellSize = useCallback(() => {
    if (boardContainerRef.current) {
      const containerWidth = boardContainerRef.current.clientWidth;
      const calculatedSize = Math.floor((containerWidth - 20) / n);
      setCellSize(Math.max(20, Math.min(60, calculatedSize)));
    }
  }, [n]);

  // 监听窗口大小变化和容器宽度变化
  useEffect(() => {
    calculateCellSize();
    const handleResize = () => calculateCellSize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateCellSize]);

  // 当 n 变化时重新计算格子大小
  useEffect(() => {
    calculateCellSize();
  }, [n, calculateCellSize]);

  // 处理被消除的滑块的动画
  const handleEliminatedSliders = (eliminatedSliders) => {
    if (eliminatedSliders && eliminatedSliders.length > 0) {
      eliminatedSliders.forEach(eliminatedId => {
        setAnimatingSliders(prev => new Set([...prev, eliminatedId]));
        // 1秒后移除动画状态
        setTimeout(() => {
          setAnimatingSliders(prev => {
            const newSet = new Set(prev);
            newSet.delete(eliminatedId);
            return newSet;
          });
        }, 1000);
      });
    }
  };

  // 初始化游戏
  useEffect(() => {
    const newGame = new SliderGame(m, n, k);
    setGame(newGame);
    setGameState(newGame.getGameState());
    setInitialSliders(newGame.sliders.map(s => [...s]));
    setMoveHistory([]);
    setCurrentStepIndex(-1);
    
    const onMoveCallback = (eliminatedSliders = []) => {
      setGameState(newGame.getGameState());
      handleEliminatedSliders(eliminatedSliders);
    };
    
    const onMoveRecordCallback = (sliderId) => {
      setMoveHistory(prevHistory => {
        const newHistory = [...prevHistory];
        newHistory.push(sliderId);
        setCurrentStepIndex(newHistory.length - 1);
        return newHistory;
      });
    };
    
    setAutoSolver(new AutoSolver(newGame, onMoveCallback, onMoveRecordCallback));
    setIsSolving(false);
    
    // 调试信息
    console.log('[SliderGame] 游戏初始化完成:', {
      m, n, k,
      sliders: newGame.sliders,
      grid: newGame.grid
    });
  }, [m, n, k]);

  // 应用新参数并重新开始游戏
  const handleRestart = () => {
    const validM = Math.max(gameConfig.minRows, Math.min(gameConfig.maxRows, tempM));
    const validN = Math.max(gameConfig.minCols, Math.min(gameConfig.maxCols, tempN));
    const maxK = Math.floor(validM * validN / gameConfig.sliderLength * 0.8);
    const validK = Math.max(1, Math.min(maxK, tempK));
    
    setM(validM);
    setN(validN);
    setK(validK);
    setTempM(validM);
    setTempN(validN);
    setTempK(validK);

    const newGame = new SliderGame(validM, validN, validK);
    setGame(newGame);
    setGameState(newGame.getGameState());
    setInitialSliders(newGame.sliders.map(s => [...s]));
    setMoveHistory([]);
    setCurrentStepIndex(-1);
    setAnimatingSliders(new Set());
    
    const onMoveCallback = (eliminatedSliders = []) => {
      setGameState(newGame.getGameState());
      handleEliminatedSliders(eliminatedSliders);
    };
    
    const onMoveRecordCallback = (sliderId) => {
      setMoveHistory(prevHistory => {
        const newHistory = [...prevHistory];
        newHistory.push(sliderId);
        setCurrentStepIndex(newHistory.length - 1);
        return newHistory;
      });
    };
    
    setAutoSolver(new AutoSolver(newGame, onMoveCallback, onMoveRecordCallback));
    setIsSolving(false);
  };

  // 处理滑块点击
  const handleSliderClick = (sliderId) => {
    if (game && !gameState.gameOver) {
      const debugMessage = (msg, enable) => enable && console.log(`[SliderGame] ${msg}`);
      debugMessage(`点击滑块 ${sliderId}`, true);
      
      const slider = game.sliders.find(s => s[0] === sliderId);
      if (slider) {
        const canMove = game.canMoveSlider(slider);
        debugMessage(`滑块 ${sliderId} 可移动: ${canMove}`, true);
        
        if (canMove) {
          const result = game.moveSlider(sliderId);
          debugMessage(`移动滑块 ${sliderId} 结果: ${result.success}`, true);
          
          if (result.success) {
            // 记录移动历史
            const newHistory = moveHistory.slice(0, currentStepIndex + 1);
            newHistory.push(sliderId);
            setMoveHistory(newHistory);
            setCurrentStepIndex(newHistory.length - 1);
            
            // 获取新的游戏状态
            const newState = game.getGameState();
            
            // 处理被消除的滑块的动画
            handleEliminatedSliders(result.eliminatedSliders);
            
            setGameState(newState);
          }
        } else {
          debugMessage(`滑块 ${sliderId} 无法移动`, true);
        }
      }
    }
  };

  // 自动求解
  const handleAutoSolve = async () => {
    console.log('[SliderGameApp] ===== 自动求解按钮被点击 =====');
    console.log('[SliderGameApp] autoSolver存在:', !!autoSolver);
    console.log('[SliderGameApp] isSolving:', isSolving);
    console.log('[SliderGameApp] gameState.gameOver:', gameState?.gameOver);
    
    if (!autoSolver || isSolving || gameState.gameOver) {
      console.log('[SliderGameApp] 自动求解条件不满足，退出');
      return;
    }

    console.log('[SliderGameApp] 开始自动求解');
    setIsSolving(true);

    await autoSolver.solve(500);

    console.log('[SliderGameApp] 自动求解完成');
    setIsSolving(false);
  };

  // 终止求解
  const handleStopSolve = () => {
    if (autoSolver && isSolving) {
      console.log('[SliderGameApp] ===== 终止求解按钮被点击 =====');
      autoSolver.stop();
    }
  };

  // 从初始状态和移动历史重建游戏状态
  const rebuildGameState = (fromStep) => {
    if (initialSliders.length === 0) return null;

    const newGame = new SliderGame(m, n, k);
    newGame.sliders = initialSliders.map(s => [...s]);
    newGame.grid = Array(m).fill().map(() => Array(n).fill(0));
    
    // 重建网格
    newGame.sliders.forEach(slider => {
      if (slider[5] === 1) {
        const cells = newGame.getSliderCells(slider);
        cells.forEach(([x, y]) => {
          newGame.grid[x][y] = slider[0];
        });
      }
    });

    // 执行到指定步骤
    const stepsToExecute = fromStep === -1 ? [] : moveHistory.slice(0, fromStep + 1);
    for (const sliderId of stepsToExecute) {
      newGame.moveSlider(sliderId);
    }

    return newGame;
  };

  // 更新autoSolver以使用新的game对象
  const updateAutoSolver = (newGame, currentStep) => {
    const onMoveCallback = (eliminatedSliders = []) => {
      setGameState(newGame.getGameState());
      handleEliminatedSliders(eliminatedSliders);
    };
    
    const onMoveRecordCallback = (sliderId) => {
      setMoveHistory(prevHistory => {
        const newHistory = [...prevHistory];
        newHistory.push(sliderId);
        setCurrentStepIndex(newHistory.length - 1);
        return newHistory;
      });
    };
    
    setAutoSolver(new AutoSolver(newGame, onMoveCallback, onMoveRecordCallback));
  };

  // 后退一步
  const handleUndo = () => {
    if (currentStepIndex < 0 || isSolving) return;

    const newGame = rebuildGameState(currentStepIndex - 1);
    if (newGame) {
      setGame(newGame);
      setGameState(newGame.getGameState());
      setCurrentStepIndex(currentStepIndex - 1);
      setAnimatingSliders(new Set());
      updateAutoSolver(newGame, currentStepIndex - 1);
    }
  };

  // 前进一步
  const handleRedo = () => {
    if (currentStepIndex >= moveHistory.length - 1 || isSolving) return;

    const newGame = rebuildGameState(currentStepIndex + 1);
    if (newGame) {
      setGame(newGame);
      setGameState(newGame.getGameState());
      setCurrentStepIndex(currentStepIndex + 1);
      setAnimatingSliders(new Set());
      updateAutoSolver(newGame, currentStepIndex + 1);
    }
  };

  // 获取滑块样式
        const getSliderStyle = (slider) => {
          const [id, headX, headY, dx, dy, alive] = slider;
          const isAnimating = animatingSliders.has(id);
          
          // 已死亡且不在动画中的滑块不显示
          if (!alive && !isAnimating) return null;

          const tailX = headX - dx * (gameConfig.sliderLength - 1);
          const tailY = headY - dy * (gameConfig.sliderLength - 1);

          // 计算滑块的方向和形状
          let top, left, width, height;

          if (dx !== 0) {
            // 垂直方向
            top = Math.min(headX, tailX) * cellSize;
            left = headY * cellSize;
            width = cellSize * 0.8;
            height = cellSize * (gameConfig.sliderLength - 0.2);
          } else {
            // 水平方向
            top = headX * cellSize;
            left = Math.min(headY, tailY) * cellSize;
            width = cellSize * (gameConfig.sliderLength - 0.2);
            height = cellSize * 0.8;
          }

          const canMove = alive && game.canMoveSlider(slider);
          
          // 分别处理位置和透明度的过渡动画
          let transition = 'all 0.3s ease';
          if (isAnimating) {
            // 如果在死亡动画中，添加透明度过渡，但保留位置过渡
            transition = 'opacity 1s ease-in-out, all 0.3s ease';
          }
          
          return {
            position: 'absolute',
            top: `${top + cellSize * 0.1}px`,
            left: `${left + cellSize * 0.1}px`,
            width: `${width}px`,
            height: `${height}px`,
            backgroundColor: `hsl(${id * 60}, 70%, ${canMove ? '60%' : '40%'})`,
            cursor: canMove ? 'pointer' : 'not-allowed',
            opacity: isAnimating ? 0 : (alive ? 1 : 0.5),
            zIndex: 10,
            borderRadius: '8px',
            border: `2px solid ${canMove ? '#333' : '#999'}`,
            boxShadow: canMove ? '0 4px 12px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.2)',
            transition: transition
          };
        };

  // 获取头部标记样式
  const getHeadMarkerStyle = (slider) => {
    const [, headX, headY, , , alive] = slider;
    const isAnimating = animatingSliders.has(slider[0]);
    
    // 已死亡且不在动画中的滑块不显示头部标记
    if (!alive && !isAnimating) return null;

    const markerSize = cellSize * 0.2;
    const offset = (cellSize - markerSize) / 2;

    // 分别处理位置和透明度的过渡动画
    let transition = 'all 0.3s ease';
    if (isAnimating) {
      // 如果在死亡动画中，添加透明度过渡，但保留位置过渡
      transition = 'opacity 1s ease-in-out, all 0.3s ease';
    }

    return {
      position: 'absolute',
      top: `${headX * cellSize + offset}px`,
      left: `${headY * cellSize + offset}px`,
      width: `${markerSize}px`,
      height: `${markerSize}px`,
      backgroundColor: '#333',
      borderRadius: '50%',
      zIndex: 20,
      border: '2px solid #fff',
      boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
      transition: transition,
      opacity: isAnimating ? 0 : 1,
      pointerEvents: 'none'
    };
  };

  if (!gameState) {
    return <div className="loading">加载中...</div>;
  }

  return (
    <div className="slider-game-app">
      <h1 style={{textAlign: 'center'}}>滑块游戏</h1>

      <div className="game-controls">
        <div className="settings-group">
          <div className="control-group">
            <label>行数:</label>
            <input
              type="number"
              value={tempM}
              onChange={handleMChange}
              onBlur={handleMBlur}
              ref={inputMRef}
            />
          </div>
          <div className="control-group">
            <label>列数:</label>
            <input
              type="number"
              value={tempN}
              onChange={handleNChange}
              onBlur={handleNBlur}
              ref={inputNRef}
            />
          </div>
          <div className="control-group">
            <label>滑块数:</label>
            <input
              type="number"
              value={tempK}
              onChange={handleKChange}
              onBlur={handleKBlur}
              ref={inputKRef}
            />
          </div>
        </div>
        <div className="actions-group">
          <button onClick={handleRestart} className="restart-btn">
            重新开始
          </button>
        </div>
      </div>
      <div className="game-header">
        <div className="game-info">
          <div className="info-item">
            <span>步数: {gameState.steps}</span>
          </div>
          <div className="info-item">
            <span>剩余滑块: {gameState.remainingSliders}/{gameState.totalSliders}</span>
          </div>
          {gameState.gameOver && (
            <div className="game-over">
              {gameState.remainingSliders === 0 ? '恭喜通关！' : '游戏结束！'}
            </div>
          )}
        </div>
      </div>
      
      <div className="history-controls">
        <button 
          onClick={handleUndo} 
          className="undo-btn"
          disabled={currentStepIndex < 0 || isSolving}
        >
          后退
        </button>
        <button 
          onClick={handleRedo} 
          className="redo-btn"
          disabled={currentStepIndex >= moveHistory.length - 1 || isSolving}
        >
          前进
        </button>
      </div>

      <div className="game-board-container" ref={boardContainerRef}>
        <div 
          className="game-board"
          style={{
            width: `${n * cellSize}px`,
            height: `${m * cellSize}px`,
            overflow: 'visible'
          }}
        >
          {/* 网格背景 */}
          <div className="grid-background">
            {Array(m).fill().map((_, i) =>
              Array(n).fill().map((_, j) => (
                <div
                key={`${i}-${j}`}
                className={`grid-cell ${i === 0 || i === m - 1 || j === 0 || j === n - 1 ? 
                  (i === 0 && j === 0 ? 'boundary-cell top-boundary-cell left-boundary-cell' : 
                   i === 0 && j === n - 1 ? 'boundary-cell top-boundary-cell right-boundary-cell' : 
                   i === m - 1 && j === 0 ? 'boundary-cell bottom-boundary-cell left-boundary-cell' : 
                   i === m - 1 && j === n - 1 ? 'boundary-cell bottom-boundary-cell right-boundary-cell' : 
                   i === 0 ? 'boundary-cell top-boundary-cell' : 
                   i === m - 1 ? 'boundary-cell bottom-boundary-cell' : 
                   j === 0 ? 'boundary-cell left-boundary-cell' : 
                   j === n - 1 ? 'boundary-cell right-boundary-cell' : 'boundary-cell') : ''}`}
                style={{
                  top: `${i * cellSize}px`,
                  left: `${j * cellSize}px`,
                  width: `${cellSize}px`,
                  height: `${cellSize}px`
                }}
              >
                {/* 在边界格子上添加箭头 */}
                {i === 0 && j === 0 && ( // 左上角
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowUpLeft style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {i === 0 && j === n - 1 && ( // 右上角
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowUpRight style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {i === m - 1 && j === 0 && ( // 左下角
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowDownLeft style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {i === m - 1 && j === n - 1 && ( // 右下角
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowDownRight style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {i === 0 && j > 0 && j < n - 1 && ( // 上边界（非角落）
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowUp style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {i === m - 1 && j > 0 && j < n - 1 && ( // 下边界（非角落）
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowDown style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {j === 0 && i > 0 && i < m - 1 && ( // 左边界（非角落）
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowLeft style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
                {j === n - 1 && i > 0 && i < m - 1 && ( // 右边界（非角落）
                  <div style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    pointerEvents: 'none',
                    zIndex: 5
                  }}>
                    <CharmArrowRight style={{ width: cellSize * 0.4, height: cellSize * 0.4, color: '#ddd' }} />
                  </div>
                )}
              </div>
              ))
            )}
          </div>

          {/* 滑块 */}
          {gameState.sliders.map((slider) => {
            const style = getSliderStyle(slider);
            if (!style) return null;

            return (
              <div
                key={slider[0]}
                className="slider"
                style={style}
                onClick={() => handleSliderClick(slider[0])}
              />
            );
          })}

          {/* 头部标记 */}
          {gameState.sliders.map((slider) => {
            const style = getHeadMarkerStyle(slider);
            if (!style) return null;

            return (
              <div
                key={`head-${slider[0]}`}
                className="head-marker"
                style={style}
              />
            );
          })}
        </div>
      </div>

      {isSolving ? (
        <button 
          onClick={handleStopSolve} 
          className="stop-solve-btn"
        >
          终止求解
        </button>
      ) : (
        <button 
          onClick={handleAutoSolve} 
          className="auto-solve-btn"
          disabled={gameState.gameOver}
        >
          自动求解
        </button>
      )}

      <div className="instruction-buttons">
        <button 
          className={`instruction-btn ${instructionType === 'game' ? 'active' : ''}`}
          onClick={() => setInstructionType(instructionType === 'game' ? null : 'game')}
        >
          游戏说明
        </button>
        <button 
          className={`instruction-btn ${instructionType === 'solver' ? 'active' : ''}`}
          onClick={() => setInstructionType(instructionType === 'solver' ? null : 'solver')}
        >
          求解说明
        </button>
      </div>

      {instructionType && (
        <div className="game-instructions">
          {instructionType === 'game' ? (
            <>
              <h3>游戏说明：</h3>
              <ul>
                <li>点击滑块使其沿头部方向移动一格</li>
                <li>滑块头部触碰边界时会被消除</li>
                <li>消除所有滑块即可获胜</li>
              </ul>
            </>
          ) : (
            <>
              <h3>求解说明：</h3>
              <ul>
                <li>自动求解算法基于运动价值计算</li>
                <li>运动价值 = 尾巴格子的两侧指向该格的滑块数量 - 头部前方一格的两侧指向该格的滑块数量</li>
                <li>算法优先移动运动价值最高的滑块</li>
                <li>每次移动后重新计滑块的运动价值</li>
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SliderGameApp;